import { RadioGroup } from '@headlessui/react'
import { zodResolver } from '@hookform/resolvers/zod'
import { Prisma } from '@prisma/client'
import {
	json,
	type ActionFunctionArgs,
	type MetaFunction,
} from '@remix-run/node'
import { Form, useActionData } from '@remix-run/react'
import { useId } from 'react'
import * as React from 'react'
import { Controller, useFieldArray } from 'react-hook-form'
import { getValidatedFormData, useRemixForm } from 'remix-hook-form'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { ErrorList, Field } from '#app/components/forms.tsx'
import { Spacer } from '#app/components/spacer.tsx'
import { Button } from '#app/components/ui/button'
import { Checkbox } from '#app/components/ui/checkbox'
import { Label } from '#app/components/ui/label'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { prisma } from '#app/utils/db.server'
import { sendDiscordMessage } from '#app/utils/discord.server'
import { cn, useIsPending } from '#app/utils/misc.tsx'
import { redirectWithToast } from '#app/utils/toast.server'

// ---------------------------------------------------------------------------------------------------------------------
type ListOfErrors = Array<string | null | undefined> | null | undefined

enum Meal {
	Beef = 'beef',
	Fish = 'fish',
	Vegetarian = 'vegetarian',
	Kid = 'kid',
}

enum Relationship {
	Bride = 'bride',
	Groom = 'groom',
	Friend = 'friend',
}

// ---------------------------------------------------------------------------------------------------------------------
interface RadioFieldProps {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	errors?: ListOfErrors
	className?: string
	options: Array<string>
	value: string
	onChange(value: string): void
}

function RadioField({
	labelProps,
	className,
	errors,
	options,
	value,
	onChange,
}: RadioFieldProps) {
	const id = useId()
	const errorId = errors?.length ? `${id}-error` : undefined

	return (
		<div className={cn(className, 'flex flex-col gap-2')}>
			<Label htmlFor={id} {...labelProps} />
			<RadioGroup value={value ?? ''} onChange={onChange}>
				<div className="relative -space-y-px rounded-md bg-white">
					{options.map((plan, planIdx) => (
						<RadioGroup.Option
							key={planIdx}
							value={plan}
							className={({ checked }) =>
								cn(
									planIdx === 0 ? 'rounded-tl-md rounded-tr-md' : '',
									planIdx === options.length - 1
										? 'rounded-bl-md rounded-br-md'
										: '',
									checked
										? 'z-10 border-indigo-200 bg-indigo-50'
										: 'border-gray-200',
									'relative flex cursor-pointer flex-col border p-3 focus:outline-none md:pl-4 md:pr-6',
								)
							}
						>
							{({ active, checked }) => (
								<span className="flex items-center text-body-sm">
									<span
										className={cn(
											checked
												? 'border-transparent bg-indigo-600'
												: 'border-gray-300 bg-white',
											active ? 'ring-2 ring-indigo-600 ring-offset-2' : '',
											'flex h-4 w-4 items-center justify-center rounded-full border',
										)}
										aria-hidden="true"
									>
										<span className="h-1.5 w-1.5 rounded-full bg-white" />
									</span>
									<RadioGroup.Label
										as="span"
										className={cn(
											checked ? 'text-indigo-900' : 'text-gray-900',
											'ml-3 text-body-md font-medium',
										)}
									>
										{plan}
									</RadioGroup.Label>
								</span>
							)}
						</RadioGroup.Option>
					))}
				</div>
			</RadioGroup>

			<div className="min-h-[32px] px-4 pb-3 pt-1">
				{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}

// ---------------------------------------------------------------------------------------------------------------------
const RSVPSchema = z.object({
	name: z.string().min(1),
	email: z.string().email().min(1),
	plusOne: z
		.object({
			hasPlusOne: z.boolean(),
			name: z.string().optional(),
			meal: z.string().optional(),
		})
		.superRefine((data, ctx) => {
			if (data.hasPlusOne) {
				if (!data.name) {
					ctx.addIssue({
						code: 'custom',
						message: 'Name is required',
						path: ['plusOne', 'name'],
					})
				}

				if (!data.meal) {
					ctx.addIssue({
						code: 'custom',
						message: 'Meal is required',
						path: ['plusOne', 'meal'],
					})
				}
			}
		}),
	phoneNumber: z.string().min(1),
	meal: z.string(),
	_redirectTo: z.string().optional(),
	relationship: z.string(),
	isAttending: z.boolean(),
	kids: z.object({
		hasKids: z.boolean(),
		kids: z.array(
			z.object({
				name: z.string(),
				meal: z.string(),
			}),
		),
	}),
})

type RSVPFormData = z.infer<typeof RSVPSchema>

const resolver = zodResolver(RSVPSchema)

export async function action({ request }: ActionFunctionArgs) {
	const {
		errors,
		data,
		receivedValues: defaultValues,
	} = await getValidatedFormData<RSVPFormData>(request, resolver)

	if (errors) {
		// The keys "errors" and "defaultValue" are picked up automatically by useRemixForm
		return json({ errors, defaultValues })
	}

	const { email, kids, meal, name, plusOne, relationship, phoneNumber } = data

	console.log({ data })

	try {
		const rsvp = await prisma.rSVP.create({
			data: {
				email,
				meal,
				name,
				relationship,
				phone: phoneNumber,
				plusOne: plusOne.hasPlusOne,
				plusOneName: plusOne.name,
				plusOneMeal: plusOne.meal,
				kids: JSON.stringify(kids),
				isAttending: data.isAttending,
			},
		})

		sendDiscordMessage(rsvp)
	} catch (error) {
		if (error instanceof Prisma.PrismaClientKnownRequestError) {
			if (error.code === 'P2002') {
				return json({ result: 'error' }, { status: 400 })
			}
		}

		return json({ result: 'error' }, { status: 400 })
	}

	return redirectWithToast('/', {
		description: "Successfully RSVP'd",
		type: 'success',
	})
}

// ---------------------------------------------------------------------------------------------------------------------
const ATTENDING_OPTIONS = [
	'Yes, I will be attending',
	'No, I will not be attending',
]

// ---------------------------------------------------------------------------------------------------------------------
export default function RSVPRoute() {
	const isPending = useIsPending()
	const actionData = useActionData<typeof action>()

	console.log({ actionData })

	const {
		handleSubmit,
		register,
		control,
		formState: { errors },
		watch,
	} = useRemixForm<z.infer<typeof RSVPSchema>>({
		resolver,
		defaultValues: {
			_redirectTo: '',
			email: '',
			kids: {
				hasKids: false,
				kids: [],
			},
			meal: Meal.Beef,
			name: '',
			phoneNumber: '',
			plusOne: {
				hasPlusOne: false,
				name: '',
				meal: Meal.Beef,
			},
			relationship: Relationship.Bride,
			isAttending: true,
		},
	})
	const { fields, append, remove } = useFieldArray({
		control,
		name: 'kids.kids',
	})

	const hasPlusOne = watch('plusOne.hasPlusOne')
	const isAttending = watch('isAttending')

	return (
		<div className="flex min-h-full flex-col justify-center pb-32 pt-20 md:pt-32">
			<div className="mx-auto w-full max-w-md lg:max-w-lg">
				<div className="flex flex-col gap-3 text-center">
					<h1 className="text-h1">RSVP</h1>
					<p className="text-body-md text-muted-foreground">
						Please enter your details.
					</p>
				</div>

				<Spacer size="xs" />

				<div>
					<div className="mx-auto w-full max-w-md px-8 lg:max-w-lg">
						<Form method="POST" onSubmit={handleSubmit}>
							<HoneypotInputs />

							{/* Name */}
							<Field
								labelProps={{ children: 'Name', className: 'text-body-md' }}
								inputProps={{
									...register('name', { required: true }),
									autoFocus: true,
									className: 'text-body-md',
									autoComplete: 'name',
								}}
								errors={[errors.name?.message]}
							/>

							{/* Email */}
							<Field
								labelProps={{ children: 'Email', className: 'text-body-md' }}
								inputProps={{
									...register('email', { required: true }),
									type: 'email',
									className: 'text-body-md',
								}}
								errors={[errors.email?.message]}
							/>

							{/* Phone number input */}
							<Field
								labelProps={{
									children: 'Phone Number',
									className: 'text-body-md',
								}}
								inputProps={{
									...register('phoneNumber', { required: true }),
									type: 'number',
									className: 'text-body-md',
								}}
								errors={[errors.phoneNumber?.message]}
							/>

							{/* Yes / No attending */}
							<Controller
								control={control}
								name="isAttending"
								render={({ field }) => (
									<RadioField
										labelProps={{
											children: 'Will you be attending?',
											className: 'text-body-md',
										}}
										options={ATTENDING_OPTIONS}
										onChange={d => field.onChange(d === ATTENDING_OPTIONS[0])}
										value={
											field.value
												? 'Yes, I will be attending'
												: 'No, I will not be attending'
										}
									/>
								)}
							/>

							{isAttending && (
								<React.Fragment>
									<Controller
										control={control}
										name="relationship"
										render={({ field, fieldState }) => (
											<RadioField
												labelProps={{
													children: 'Relationship',
													className: 'text-body-md',
												}}
												options={Object.values(Relationship)}
												errors={[fieldState.error?.message]}
												value={field.value}
												onChange={field.onChange}
											/>
										)}
									/>

									<Controller
										control={control}
										name="meal"
										render={({ field, fieldState }) => (
											<RadioField
												labelProps={{
													children: 'Dietary',
													className: 'text-body-md',
												}}
												options={Object.values(Meal)}
												errors={[fieldState.error?.message]}
												value={field.value}
												onChange={field.onChange}
											/>
										)}
									/>

									<Controller
										control={control}
										name="plusOne.hasPlusOne"
										render={({ field, fieldState }) => (
											<div className="flex items-center gap-2">
												<Checkbox
													checked={field.value}
													onCheckedChange={field.onChange}
													onBlur={field.onBlur}
													type="button"
												/>
												<label
													htmlFor="plusOne"
													className="self-center text-body-md text-muted-foreground"
												>
													Plus One?
												</label>
											</div>
										)}
									/>

									{hasPlusOne && (
										<div className="ml-3">
											<Field
												labelProps={{
													children: 'Name',
													className: 'text-body-md',
												}}
												inputProps={{
													...register('plusOne.name', { required: true }),
													autoFocus: true,
													className: 'text-body-md',
												}}
												errors={[errors.plusOne?.name?.message]}
											/>

											<Controller
												control={control}
												name="plusOne.meal"
												render={({ field, fieldState }) => (
													<RadioField
														labelProps={{
															children: 'Dietary',
															className: 'text-body-md',
														}}
														options={Object.values(Meal)}
														errors={[fieldState.error?.message]}
														value={field.value ?? ''}
														onChange={field.onChange}
													/>
												)}
											/>
										</div>
									)}

									<div className="flex justify-end">
										<Button
											type="button"
											onClick={() => append({ meal: Meal.Kid, name: '' })}
										>
											Add Kid
										</Button>
									</div>

									{fields.map((field, index) => (
										<div
											key={field.id}
											className="my-2 flex flex-col gap-3 rounded-sm border p-4"
										>
											<Field
												labelProps={{
													children: 'Name',
													className: 'text-body-md',
												}}
												inputProps={{
													...register(`kids.kids.${index}.name`, {
														required: true,
													}),
													autoFocus: true,
													className: 'text-body-md',
												}}
												errors={[errors.kids?.kids?.[index]?.name?.message]}
											/>

											<Controller
												control={control}
												name={`kids.kids.${index}.meal`}
												render={({ field, fieldState }) => (
													<RadioField
														labelProps={{
															children: 'Dietary',
															className: 'text-body-md',
														}}
														options={Object.values(Meal)}
														errors={[fieldState.error?.message]}
														value={field.value ?? ''}
														onChange={field.onChange}
													/>
												)}
											/>

											<Button onClick={() => remove(index)}>Remove</Button>
										</div>
									))}
								</React.Fragment>
							)}

							<div className="flex items-center justify-between gap-6 pt-3">
								<StatusButton
									className="w-full text-body-md"
									status={isPending ? 'pending' : 'idle'}
									type="submit"
									disabled={isPending}
								>
									RSVP
								</StatusButton>
							</div>
						</Form>
					</div>
				</div>
			</div>
		</div>
	)
}

export const meta: MetaFunction = () => {
	return [{ title: 'RSVP' }]
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
